import { type Paddle, initializePaddle } from "@paddle/paddle-js";
import { logger } from "@/utils/logger";

/**
 * Client Paddle partagé (checkout + price preview).
 *
 * ⚠️ L'environnement est LU depuis `VITE_PADDLE_ENV` et échoue BRUYAMMENT s'il
 * est absent/invalide — jamais de défaut silencieux, pour ne jamais viser le
 * mauvais compte Paddle (sandbox vs production).
 *
 * Le token client (`VITE_PADDLE_CLIENT_TOKEN`) et les price IDs sont lus depuis
 * l'environnement, jamais codés en dur. La clé API SERVEUR n'apparaît jamais ici
 * (le portail passe par une edge function Supabase).
 */

const TOKEN = import.meta.env.VITE_PADDLE_CLIENT_TOKEN as string | undefined;

export const PRICE_IDS = {
  premium: {
    monthly:
      (import.meta.env.VITE_PADDLE_PREMIUM_MONTHLY as string | undefined) ?? "",
    yearly:
      (import.meta.env.VITE_PADDLE_PREMIUM_YEARLY as string | undefined) ?? "",
  },
  pro: {
    monthly:
      (import.meta.env.VITE_PADDLE_PRO_MONTHLY as string | undefined) ?? "",
    yearly:
      (import.meta.env.VITE_PADDLE_PRO_YEARLY as string | undefined) ?? "",
  },
} as const;

/** Tous les priceId non vides — pour Paddle.PricePreview(). */
export const ALL_PRICE_IDS: string[] = [
  PRICE_IDS.premium.monthly,
  PRICE_IDS.premium.yearly,
  PRICE_IDS.pro.monthly,
  PRICE_IDS.pro.yearly,
].filter((id): id is string => Boolean(id));

function resolvePaddleEnvironment(): "sandbox" | "production" {
  const env = import.meta.env.VITE_PADDLE_ENV as string | undefined;
  if (env === "sandbox" || env === "production") return env;
  // Fail loud : ne JAMAIS deviner l'environnement Paddle silencieusement.
  throw new Error(
    "VITE_PADDLE_ENV manquant ou invalide (attendu 'sandbox' ou 'production'). " +
      "Définis-le dans .env pour ne jamais viser le mauvais compte Paddle.",
  );
}

let paddleInstance: Paddle | null = null;

export async function getPaddle(): Promise<Paddle | null> {
  if (paddleInstance) return paddleInstance;
  if (!TOKEN) {
    logger.error("VITE_PADDLE_CLIENT_TOKEN manquant — Paddle désactivé");
    return null;
  }
  const environment = resolvePaddleEnvironment(); // throw si non défini → fail loud
  if (TOKEN.startsWith("test_") && environment === "production") {
    logger.error(
      "⚠️ Incohérence Paddle : token sandbox (test_) avec VITE_PADDLE_ENV=production",
    );
  }
  paddleInstance =
    (await initializePaddle({ environment, token: TOKEN })) ?? null;
  return paddleInstance;
}

/**
 * Paddle Retain : identifie le client Paddle connecté (pwCustomer).
 * ⚠️ Doit être l'ID client Paddle (`ctm_…`), jamais notre user_id ni un email.
 */
export async function setPaddleRetainCustomer(
  paddleCustomerId: string,
): Promise<void> {
  if (!paddleCustomerId.startsWith("ctm_")) return;
  const paddle = await getPaddle();
  if (!paddle) return;
  try {
    paddle.Update({ pwCustomer: { id: paddleCustomerId } });
  } catch (err) {
    logger.error("Paddle.Update(pwCustomer) a échoué", err);
  }
}
